import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    GuestCount,
    Reservation,
    ReservationStatus,
    TableReservationEvent
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {AgeCategory, AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {FloorplanReservationItemDTO} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {FloorplanListHalfboardDTO} from "crqlar-react-common/dist/models/floorplan/floorplanListHalfboard.dto";
import {
    endOfDay,
    formatDateHMRange,
    formatDayMonth,
    formatFullDMY,
    isSameDay,
    startOfDay
} from "crqlar-react-common/dist/utils/date.util";
import {
    formatProfileListName,
    getProfileAllergies,
    getProfileAllergiesString,
    getProfileFoodDiets,
    getProfileFoodDietsString,
    getProfileMedical,
    getProfileMedicalString,
} from "crqlar-react-common/dist/utils/profile.util";
import {COMMON_PREFERENCE_TYPES, Preference} from "crqlar-react-common/dist/models/profile/preference.model";
import {labelizeUnderScoreText} from "crqlar-react-common/dist/utils/text.util";
import {getReservationDisplayGuestCounts} from "crqlar-react-common/dist/utils/reservationGuestCounts.util";
import {
    getOrderedReservationProfileIds,
    getPrimaryProfile,
    getPrimaryProfileId,
} from "crqlar-react-common/dist/utils/reservationProfile.util";
import NoFoodIcon from "@mui/icons-material/NoFood";
import SetMealIcon from "@mui/icons-material/SetMeal";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import React from "react";

export const ALLERGY_ICON_MAP: Record<string, {
    icon: React.ElementType;
    color: string
}> = {
    [COMMON_PREFERENCE_TYPES.CRQLAR_FOOD_ALLERGY]: {
        icon: NoFoodIcon,
        color: "#F25C05"
    },
    [COMMON_PREFERENCE_TYPES.CRQLAR_FOOD_TYPE]: {
        icon: SetMealIcon,
        color: "#4CAF50"
    },
    [COMMON_PREFERENCE_TYPES.CRQLAR_MEDICAL]: {
        icon: HealthAndSafetyIcon,
        color: "#7B5FA4"
    },
};

// Re-exported so floorplan components keep a single import for reservation display helpers.
export {getOrderedReservationProfileIds, getPrimaryProfile, getPrimaryProfileId};

export const getOrderedFloorplanReservationProfiles = (
    reservationItem: FloorplanReservationItemDTO,
): Profile[] => {
    const reservation = reservationItem.restaurantReservation;
    const profilesById = new Map(
        reservationItem.profiles
            .filter(profile => Boolean(profile.id))
            .map(profile => [profile.id!, profile]),
    );
    const orderedProfileIds = Array.from(new Set([
        getPrimaryProfileId(reservation) ?? reservationItem.profiles[0]?.id,
        ...getOrderedReservationProfileIds(reservation),
    ].filter((profileId): profileId is string => Boolean(profileId))));
    const orderedProfiles = orderedProfileIds
        .map(profileId => profilesById.get(profileId))
        .filter((profile): profile is Profile => Boolean(profile));

    return orderedProfiles.length > 0 ? orderedProfiles : reservationItem.profiles;
};

export const getGuestCount = (reservation?: Reservation): number =>
    (reservation?.guestCounts ?? []).reduce((sum, gc) => sum + (gc.count ?? 0), 0);

export const getFloorplanGuestCounts = (item?: FloorplanReservationItemDTO): GuestCount[] =>
    item?.displayGuestCounts ?? getReservationDisplayGuestCounts(item?.restaurantReservation);

export const getFloorplanGuestCount = (item?: FloorplanReservationItemDTO): number =>
    getFloorplanGuestCounts(item).reduce((sum, guestCount) => sum + (guestCount.count ?? 0), 0);

const getStayStart = (reservation?: Reservation): Date | undefined =>
    reservation?.actualStartDate
        ? new Date(reservation.actualStartDate)
        : reservation?.expectedStartDate
            ? new Date(reservation.expectedStartDate)
            : undefined;

const getStayEnd = (reservation?: Reservation): Date | undefined =>
    reservation?.actualEndDate
        ? new Date(reservation.actualEndDate)
        : reservation?.expectedEndDate
            ? new Date(reservation.expectedEndDate)
            : undefined;

export const getAccommodationStayIndicators = (
    accommodationReservations: Reservation[],
    selectedDate: Date,
) => {
    const selectedStart = startOfDay(selectedDate).getTime();
    const selectedEnd = endOfDay(selectedDate).getTime();
    const relevantReservation = accommodationReservations.find((stayReservation) => {
        const stayStart = getStayStart(stayReservation)?.getTime();
        const stayEnd = getStayEnd(stayReservation)?.getTime();
        if (stayStart == null || stayEnd == null) return false;
        return stayStart <= selectedEnd && stayEnd >= selectedStart;
    }) ?? accommodationReservations[0];
    const stayStart = getStayStart(relevantReservation);
    const stayEnd = getStayEnd(relevantReservation);

    return {
        stayStart,
        stayEnd,
        checksInOnDate: isSameDay(stayStart, selectedDate),
        checksOutOnDate: isSameDay(stayEnd, selectedDate),
    };
};

export const getStatusColors = (status?: ReservationStatus): {
    borderColor: string;
    backgroundColor: string;
} => {
    switch (status) {
        case ReservationStatus.CONFIRMED:
            return {
                borderColor: CommonThemeColors.sys.success.main,
                backgroundColor: CommonThemeColors.sys.success.background,
            };
        case ReservationStatus.ACTIVE:
            return {
                borderColor: CommonThemeColors.sys.info.main,
                backgroundColor: CommonThemeColors.sys.info.background,
            };
        case ReservationStatus.CANCELLED:
        case ReservationStatus.NO_SHOW:
            return {
                borderColor: CommonThemeColors.sys.error.main,
                backgroundColor: CommonThemeColors.sys.error.background,
            };
        case ReservationStatus.COMPLETED:
            return {
                borderColor: CommonThemeColors.sys.palette.blueGray["600"],
                backgroundColor: CommonThemeColors.sys.palette.blueGray["50"],
            };
        case ReservationStatus.PENDING:
        default:
            return {
                borderColor: "rgba(190, 190, 190, 1)",
                backgroundColor: "rgba(250, 250, 250, 1)",
            };
    }
};

/** How many room numbers a compact row prints before the rest collapse into a "+N" suffix. */
export const INLINE_ROOM_NAME_LIMIT = 1;

/**
 * A party can legitimately hold several rooms for the same stay — a family booking two rooms,
 * or a room move recorded as a second room on the reservation — and listing them all overflows
 * the compact rows. Inline spots therefore print the first room and collapse the rest into
 * "+N"; the full list stays reachable through the hover title and the detail views (hover
 * tooltip, preview card, printed list).
 */
export const formatInlineRoomNames = (
    roomNames: string[],
    limit: number = INLINE_ROOM_NAME_LIMIT,
): string => {
    if (roomNames.length <= limit) return roomNames.join(", ");

    return `${roomNames.slice(0, limit).join(", ")} +${roomNames.length - limit}`;
};

interface GetReservationDisplayParams {
    reservation?: Reservation;
    profiles?: Profile[];
    ageCategories?: AgeCategory[];
    tableResources?: Resource[];
    accommodationResources?: Resource[];
    guestCounts?: GuestCount[];
    showDateFormat?: boolean
}

export const getReservationDisplay = ({
                                          reservation,
                                          profiles = [],
                                          ageCategories = [],
                                          tableResources = [],
                                          accommodationResources = [],
                                          guestCounts,
                                          showDateFormat = false
                                      }: GetReservationDisplayParams) => {
    const today = new Date();
    const currentReservation = (reservation ?? {}) as Reservation;

    const start = currentReservation.expectedStartDate
        ? new Date(currentReservation.expectedStartDate)
        : undefined;
    const end = currentReservation.expectedEndDate
        ? new Date(currentReservation.expectedEndDate)
        : undefined;

    // A reservation is represented by a single guest — the primary profile.
    const primaryProfile = getPrimaryProfile(currentReservation, profiles);

    const guestNames = primaryProfile
        ? [primaryProfile.lastName, primaryProfile.firstName].filter(Boolean).join(" ")
        : "";
    const guestDisplayName = primaryProfile ? formatProfileListName(primaryProfile) : "";
    const guestDisplayNames = [guestDisplayName].filter(Boolean);

    const displayedGuestCounts = guestCounts ?? currentReservation.guestCounts ?? [];
    const guestCount = displayedGuestCounts.reduce((sum, count) => sum + (count.count ?? 0), 0);

    const primaryLanguage =
        primaryProfile?.languageCode || primaryProfile?.additionalLanguages?.[0] || null;
    const status = currentReservation.status;

    const birthdayNames = profiles
        .filter((profile) => {
            if (!profile.birthDay) return false;
            // Product decision -> compare against the reservation date and not the actual current date

            const birthdayEventFlag = reservation?.tableReservationEvent === TableReservationEvent.BIRTHDAY

            // One of the conditions must be met to show a cake icon:
            // Birthday during a reservation date OR manually set birthdayEventFlag
            return formatDayMonth(new Date(profile.birthDay)) === formatDayMonth(reservation?.expectedStartDate) || birthdayEventFlag;
        })
        .map((profile) => [profile.firstName, profile.lastName].filter(Boolean).join(" "))
        .filter(Boolean);

    const allergyLabels = profiles.flatMap((profile) => [
        ...getProfileAllergiesString(profile).map((value) => labelizeUnderScoreText(value)),
        ...getProfileFoodDietsString(profile).map((value) => labelizeUnderScoreText(value)),
        ...getProfileMedicalString(profile).map((value) => labelizeUnderScoreText(value)),
    ]);

    const preferences: Preference[] = profiles.flatMap((profile) => [
        ...getProfileAllergies(profile),
        ...getProfileFoodDiets(profile),
        ...getProfileMedical(profile),
    ]);

    const ageCounts: Record<string, number> = {};
    const reservationGuestCounts = displayedGuestCounts;
    for (const guestCountItem of reservationGuestCounts) {
        if (!guestCountItem.ageCategoryId || !guestCountItem.count) continue;
        const category = ageCategories.find((value) => value.id === guestCountItem.ageCategoryId);
        const type = category?.type ?? AgeCategoryType.ADULT;
        ageCounts[type] = (ageCounts[type] ?? 0) + guestCountItem.count;
    }

    const adults = ageCounts[AgeCategoryType.ADULT] ?? 0;
    const children = (ageCounts[AgeCategoryType.CHILD] ?? 0) + (ageCounts[AgeCategoryType.TEENAGER] ?? 0);
    const infants = ageCounts[AgeCategoryType.INFANT] ?? 0;

    const timeRange = showDateFormat ? `${formatDayMonth(start)} - ${formatFullDMY(end)}` : formatDateHMRange(start, end);
    const tableNames = tableResources.map((resource) => resource.name).join(", ");
    const roomNameList = accommodationResources
        .map((resource) => resource.name?.trim())
        .filter(Boolean) as string[];
    const roomNames = roomNameList.join(", ");
    const roomNamesInline = formatInlineRoomNames(roomNameList);
    const fromBookingEngine = !!currentReservation.fromBookingEngine;
    const {backgroundColor, borderColor} = getStatusColors(status);
    const statusText = status ? labelizeUnderScoreText(status) : "—";

    return {
        // Raw date context
        today,

        // Core reservation display fields
        primaryProfile,
        guestDisplayName,
        guestDisplayNames,
        guestNames,
        guestCount,
        primaryLanguage,
        status,
        statusText,
        backgroundColor,
        borderColor,
        start,
        end,

        // Formatted display helpers
        tableNames,
        roomNameList,
        roomNames,
        roomNamesInline,
        fromBookingEngine,
        timeRange,

        // Age grouping
        ageCounts,
        adults,
        children,
        infants,

        // Profile highlights
        birthdayNames,
        isBirthday: birthdayNames.length > 0,
        allergyLabels,
        hasAllergies: allergyLabels.length > 0,
        preferences,
    };
};

export const isConflictCompleted = (c: FloorplanReservationItemDTO): boolean => {
    const status = c.restaurantReservation?.status;
    if (status === ReservationStatus.COMPLETED) return true;
    const endDate = c.restaurantReservation?.expectedEndDate;
    if (endDate && new Date(endDate).getTime() < Date.now()) return true;
    return false;
};

export function sortByStartDate(reservations: FloorplanReservationItemDTO[]): FloorplanReservationItemDTO[] {
    return [...reservations].sort((a, b) => {
        const aS = a.restaurantReservation?.expectedStartDate
            ? new Date(a.restaurantReservation.expectedStartDate).getTime() : 0;
        const bS = b.restaurantReservation?.expectedStartDate
            ? new Date(b.restaurantReservation.expectedStartDate).getTime() : 0;
        return aS - bS;
    });
}

/**
 * Merges FloorplanReservationItemDTOs that share a reservationGroupId into a
 * single item for the same sitting — combining tableResources, profiles, and guestCounts.
 * This mirrors the backend grouping logic and acts as a client-side safety net
 * so that group reservations always appear as one combined slot per table group.
 */
export function groupFloorplanReservations(
    items: FloorplanReservationItemDTO[],
): FloorplanReservationItemDTO[] {
    const groups = new Map<string, FloorplanReservationItemDTO[]>();

    for (const item of items) {
        const reservation = item.restaurantReservation;
        // A reservationGroupId can span a whole stay with separate restaurant sittings
        // (breakfast/lunch/dinner). Group only identical sittings; otherwise the active
        // sidebar and table overlays collapse later meal periods into the first one.
        const key = [
            reservation?.reservationGroupId ?? item.reservationId,
            reservation?.expectedStartDate ? new Date(reservation.expectedStartDate).getTime() : '',
            reservation?.expectedEndDate ? new Date(reservation.expectedEndDate).getTime() : '',
            reservation?.meta?.eatPeriod ?? '',
        ].join('|');
        const existing = groups.get(key) ?? [];
        existing.push(item);
        groups.set(key, existing);
    }

    return Array.from(groups.values()).map((group) => {
        if (group.length === 1) return group[0];

        const representative = group[0];

        const tableResourceMap = new Map<string, Resource>();
        for (const item of group) {
            for (const tr of item.tableResources ?? []) {
                if (tr.id) tableResourceMap.set(tr.id, tr);
            }
        }

        const profileMap = new Map<string, Profile>();
        for (const item of group) {
            for (const p of item.profiles ?? []) {
                if (p.id) profileMap.set(p.id, p);
            }
        }

        const gcMap = new Map<string, GuestCount>();
        const displayGcMap = new Map<string, GuestCount>();
        const aliasMap = new Map<string, NonNullable<FloorplanReservationItemDTO['aliasAgeCategories']>[number]>();
        const ageCategoryMap = new Map<string, AgeCategory>();
        for (const item of group) {
            for (const gc of item.restaurantReservation?.guestCounts ?? []) {
                if (!gc.ageCategoryId) continue;
                const prev = gcMap.get(gc.ageCategoryId);
                gcMap.set(gc.ageCategoryId, {
                    ageCategoryId: gc.ageCategoryId,
                    count: (prev?.count ?? 0) + (gc.count ?? 0),
                });
            }
            for (const gc of getFloorplanGuestCounts(item)) {
                if (!gc.ageCategoryId) continue;
                const prev = displayGcMap.get(gc.ageCategoryId);
                displayGcMap.set(gc.ageCategoryId, {
                    ageCategoryId: gc.ageCategoryId,
                    count: (prev?.count ?? 0) + (gc.count ?? 0),
                });
            }
            for (const alias of item.aliasAgeCategories ?? []) {
                const prev = aliasMap.get(alias.id);
                aliasMap.set(alias.id, {
                    ...alias,
                    count: (prev?.count ?? 0) + alias.count,
                });
            }
            for (const category of item.ageCategories ?? []) {
                if (category.id) ageCategoryMap.set(category.id, category);
            }
        }

        const allResourceIds = Array.from(
            new Set(group.flatMap((item) => item.restaurantReservation?.resourceIds ?? [])),
        );

        return {
            ...representative,
            tableResources: Array.from(tableResourceMap.values()),
            profiles: Array.from(profileMap.values()),
            displayGuestCounts: Array.from(displayGcMap.values()),
            aliasAgeCategories: Array.from(aliasMap.values()),
            ageCategories: Array.from(ageCategoryMap.values()),
            restaurantReservation: {
                ...representative.restaurantReservation,
                resourceIds: allResourceIds,
                guestCounts: Array.from(gcMap.values()),
            },
        };
    });
}

/**
 * The rate label shown on a halfboard row — the sidebar arrivals item, the drag preview and the
 * drop confirmation card all print the same string.
 *
 * Prefers the reservation's readable board code ("HP", "ÜF", "AI") over the accommodation
 * service name. On Casablanca properties the service is named after the opaque `PMS_XXXXXXXX` rate
 * string the OTA feed streams, which tells staff nothing; `mealCode` is what the property mapped
 * that rate to. It is unset for every other PMS and for rates nobody has mapped yet, so those keep
 * falling back to the service name exactly as before.
 */
export function getHalfboardRateLabel(item?: FloorplanListHalfboardDTO): string | undefined {
    return item?.accommodationReservation?.mealCode || item?.accommodationService?.name || undefined;
}

/**
 * The rate label shown on a seated/waitlisted restaurant reservation (sidebar card, print column).
 * The API resolves it per linked stay with the same rule as {@link getHalfboardRateLabel}; a party
 * spread over several stays on different rates lists each once.
 */
export function getReservationRateLabel(item?: FloorplanReservationItemDTO): string | undefined {
    return item?.rateCodes?.filter(Boolean).join(", ") || undefined;
}

/**
 * Merges FloorplanListHalfboardDTOs that share a reservation group into a single
 * item — combining profiles, accommodationResources, and guestCounts.
 * The result has `profiles` populated so the sidebar item can show all guest names.
 * Items whose service name matches an entry in agenciesWithIndividualReservations are
 * kept as separate slots instead of being merged.
 */
export function groupHalfboardProfiles(
    items: FloorplanListHalfboardDTO[],
    agenciesWithIndividualReservations?: string[],
): FloorplanListHalfboardDTO[] {
    const agencySet = new Set(agenciesWithIndividualReservations ?? []);
    const groups = new Map<string, FloorplanListHalfboardDTO[]>();

    for (const item of items) {
        const isIndividual = agencySet.size > 0 &&
            item.accommodationService?.name != null &&
            agencySet.has(item.accommodationService.name);
        const key = isIndividual
            ? (item.accommodationReservation?.id ?? item.profileIds?.[0])
            : (item.accommodationReservation?.reservationGroupId
                ?? item.accommodationReservation?.id
                ?? item.profileIds?.[0]);
        const existing = groups.get(key) ?? [];
        existing.push(item);
        groups.set(key, existing);
    }

    return Array.from(groups.values()).map((group) => {
        if (group.length === 1) return group[0];

        const representative = group[0];

        const profileMap = new Map<string, Profile>();
        for (const item of group) {
            for (const p of item.profiles ?? []) {
                if (p.id) profileMap.set(p.id, p);
            }
        }

        const resourceMap = new Map<string, Resource>();
        for (const item of group) {
            for (const r of item.accommodationResources ?? []) {
                if (r.id) resourceMap.set(r.id, r);
            }
        }

        const gcMap = new Map<string, GuestCount>();
        const displayGcMap = new Map<string, GuestCount>();
        const ageCategoryMap = new Map<string, AgeCategory>();
        for (const item of group) {
            for (const gc of item.accommodationReservation?.guestCounts ?? []) {
                if (!gc.ageCategoryId) continue;
                const prev = gcMap.get(gc.ageCategoryId);
                gcMap.set(gc.ageCategoryId, {
                    ageCategoryId: gc.ageCategoryId,
                    count: (prev?.count ?? 0) + (gc.count ?? 0),
                });
            }
            for (const gc of getReservationDisplayGuestCounts(item.accommodationReservation)) {
                if (!gc.ageCategoryId) continue;
                const prev = displayGcMap.get(gc.ageCategoryId);
                displayGcMap.set(gc.ageCategoryId, {
                    ageCategoryId: gc.ageCategoryId,
                    count: (prev?.count ?? 0) + (gc.count ?? 0),
                });
            }
            for (const category of item.ageCategories ?? []) {
                if (category.id) ageCategoryMap.set(category.id, category);
            }
        }

        // Union the primary too — a stay's guests are `primaryProfileId + profileIds`, and a booker
        // that a connector kept only on the primary would otherwise be lost when rows are merged.
        const allProfileIds = Array.from(
            new Set(group.flatMap((item) => [
                ...getOrderedReservationProfileIds(item.accommodationReservation),
                ...(item.profileIds ?? []),
            ])),
        );

        return {
            ...representative,
            profiles: Array.from(profileMap.values()),
            ageCategories: Array.from(ageCategoryMap.values()),
            accommodationResources: Array.from(resourceMap.values()),
            accommodationReservation: {
                ...representative.accommodationReservation,
                profileIds: allProfileIds,
                guestCounts: Array.from(gcMap.values()),
                displayGuestCounts: Array.from(displayGcMap.values()),
            },
        };
    });
}

export function findCurrentReservation(
    sortedTableRes: FloorplanReservationItemDTO[],
    refTime: Date,
): FloorplanReservationItemDTO | undefined {
    return (
        sortedTableRes.find(r => {
            const s = r.restaurantReservation?.expectedStartDate;
            const e = r.restaurantReservation?.expectedEndDate;
            if (!s || !e) return false;
            return new Date(s) <= refTime && refTime < new Date(e);
        }) ??
        sortedTableRes.find(r => {
            const s = r.restaurantReservation?.expectedStartDate;
            if (!s) return false;
            return new Date(s) > refTime;
        })
    );
}
